import React, { useState, useRef, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import SearchBar from '../components/SearchBar';
import NewIVRModal from '../components/NewIVRModal';
import ivrService from '../services/ivrService';
import { useAuth } from '../context/AuthContext';
import DeleteRecordModal from '../components/DeleteRecordModal';
import { consumeQuickAction } from '../utils/quickAction';
import { isAdmin } from '../utils/roles';
import DateRangeFilter from '../components/DateRangeFilter';
import useDebounced from '../hooks/useDebounced';
import { useT, useLocale } from '../i18n/LanguageContext';
import { requestErrorMessage } from '../utils/requestError';
import IVRDetailsModal from '../features/ivr/IVRDetailsModal';
import IVRListTable from '../features/ivr/IVRListTable';
import { IVR_TABS as tabs, IVR_TAB_STATUS_KEY, tabCount } from '../features/ivr/ivrPresentation';
import useAccessibleTabs from '../hooks/useAccessibleTabs';
import AdminBusinessContextFilter from '../components/AdminBusinessContextFilter';
import { captureSession, isSessionCurrent } from '../utils/authSession';

/* ─── Main Page ─── */
export default function IVRDetails() {
  const t = useT();
  const locale = useLocale();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [businessContext, setBusinessContext] = useState('all');
  const [distributorId, setDistributorId] = useState('');
  const [distributors, setDistributors] = useState([]);
  // The IVR request an admin has asked to delete, pending confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showNewIVR, setShowNewIVR] = useState(false);
  // Consumed in a mount effect, NOT a useState initializer: StrictMode double-invokes
  // initializers in dev, so the second invocation would find the flag already consumed and
  // return false - the quick action would silently never open on localhost. The effect also
  // double-runs in dev, but only ever sets true, so the second no-op run is harmless.
  useEffect(() => {
    if (consumeQuickAction('new_ivr')) setShowNewIVR(true);
  }, []);
  const [ivrData, setIvrData] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed list fetch used to be console.error'd and then rendered as "No IVR requests found" -
  // an outage read as an empty queue. The banner says what happened; the last rows stay on screen.
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // One request per pause in typing, not one (or two) per keystroke.
  const debouncedSearch = useDebounced(search, 300);
  // Date-submitted range; the filter button here was dead too.
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' });
  const [statusCounts, setStatusCounts] = useState({});
  const [countsUnavailable, setCountsUnavailable] = useState(false);
  // The queue is server-paginated at 20 rows. Preserve the returned total so the viewer can see
  // the queue depth and reach records beyond the newest page rather than seeing no indication
  // whether 5 or 500 existed, and records past 20 were unreachable by browsing. These track the page and
  // the depth the backend already reports.
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, totalPages: 1, limit: 20 });
  // Out-of-order responses: only the latest request may write the list (same guard as Invoices).
  const seq = useRef(0);

  const fetchData = useCallback(async () => {
    const mine = ++seq.current;
    const session = captureSession();
    setLoading(true);
    setError('');
    setStatusCounts({});
    setCountsUnavailable(false);
    try {
      const params = { page };
      if (admin && businessContext !== 'all') params.businessContext = businessContext;
      if (admin && businessContext === 'distributor' && distributorId)
        params.distributorId = distributorId;
      if (activeTab !== 'All') {
        params.status = IVR_TAB_STATUS_KEY[activeTab] || activeTab.toLowerCase();
      }
      if (debouncedSearch) params.search = debouncedSearch;
      if (dates.dateFrom) params.dateFrom = dates.dateFrom;
      if (dates.dateTo) params.dateTo = dates.dateTo;

      const { page: _page, status: _status, ...countParams } = params;
      const [ivrRes, countsRes] = await Promise.all([
        ivrService.getIVRRequests(params),
        ivrService.getStatusCounts(countParams).catch(() => null),
      ]);
      if (mine !== seq.current || !isSessionCurrent(session)) return;

      const payload = ivrRes.data?.data || {};
      const data = payload.data || [];
      setIvrData(Array.isArray(data) ? data : []);
      setPageInfo({
        total: payload.total || 0,
        totalPages: payload.totalPages || 1,
        limit: payload.limit || 20,
      });
      setStatusCounts(countsRes?.data?.data || {});
      setCountsUnavailable(!countsRes);
    } catch (err) {
      if (mine !== seq.current || !isSessionCurrent(session)) return;
      // The rows already on screen are deliberately NOT cleared: a refetch that fails should not
      // wipe a list the person was just reading.
      setError(requestErrorMessage(err, 'Could not load IVR requests.'));
    } finally {
      if (mine === seq.current && isSessionCurrent(session)) setLoading(false);
    }
  }, [activeTab, debouncedSearch, page, dates, admin, businessContext, distributorId]);

  useEffect(() => {
    fetchData();
    return () => {
      seq.current += 1;
    };
  }, [fetchData]);

  // A new search term starts from page 1 - once the debounce settles, so the reset and the fetch
  // land together rather than the reset firing a fetch of its own for the OLD term.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Clamp the page back into range when the queue shrinks beneath it. Without this, an admin who works
  // the LAST page and resolves those records refetches at a page that no longer exists: the list goes
  // blank ("No IVR requests found") with a nonsense "Showing 41-40 of 40 / Page 3 of 2", reading as
  // "queue drained" while records remain on earlier pages. totalPages is always >= 1, so this lands on a
  // real page, and the page change triggers a refetch that fills the list. (Found by adversarial review
  // of this very change.)
  useEffect(() => {
    if (page > pageInfo.totalPages) {
      setPage(pageInfo.totalPages);
    }
  }, [page, pageInfo.totalPages]);

  // Changing tab or search must return to page 1 - otherwise a filter with fewer pages than the current
  // page number shows an empty list.
  // A new request lands on the All view, whatever tab was open: its result (covered, not covered,
  // needs review) is unknown until the payer answers, so any other tab could hide the new row and
  // make the submission look like it did nothing.
  const showCreated = () => {
    if (admin) {
      onContextChange('direct', '');
      setActiveTab('All');
      setDates((current) => ({ ...current }));
      return;
    }
    if (activeTab === 'All') fetchData();
    else selectTab('All');
  };

  const selectTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };
  const onContextChange = (context, organization) => {
    seq.current += 1;
    setBusinessContext(context);
    setDistributorId(organization);
    setIvrData([]);
    setStatusCounts({});
    setPageInfo({ total: 0, totalPages: 1, limit: 20 });
    setPage(1);
  };
  const { tabListProps, getTabProps, panelProps } = useAccessibleTabs({
    tabs,
    activeTab,
    onSelect: selectTab,
    label: t('IVR views'),
  });

  const { total, totalPages, limit } = pageInfo;
  const firstRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(page * limit, total);

  return (
    <div>
      <PageHeader
        title={t('IVR Details')}
        subtitle={
          admin
            ? t('Review, verify and approve order requests from IVR details.')
            : t('Submit and track your IVR requests.')
        }
      >
        <SearchBar
          placeholder={t('Search IVR details...')}
          className="w-[243px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <DateRangeFilter
          dateFrom={dates.dateFrom}
          dateTo={dates.dateTo}
          onChange={(next) => {
            setDates(next);
            setPage(1);
          }}
        />
        <button
          onClick={() => setShowNewIVR(true)}
          className="h-[46px] px-4 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors flex items-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          {t('New IVR')}
        </button>
      </PageHeader>
      {admin && (
        <AdminBusinessContextFilter
          {...{ businessContext, distributorId, t }}
          onChange={onContextChange}
          onDistributors={setDistributors}
          hasDistributorRecords={ivrData.some((record) => record.businessContext === 'distributor')}
        />
      )}
      {countsUnavailable && (
        <p role="status" className="mb-4 text-sm text-slate-600">
          {t('IVR totals are unavailable. The list still uses the selected filters.')}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
        >
          {t(error)}
        </div>
      )}

      <div className="flex items-center gap-2 mb-6" {...tabListProps}>
        {tabs.map((tab) => {
          const count = tabCount(tab, statusCounts);
          return (
            <button
              key={tab}
              {...getTabProps(tab)}
              onClick={() => selectTab(tab)}
              className={`px-4 h-10 rounded-lg text-[13px] font-semibold transition-colors flex items-center gap-1.5 ${
                activeTab === tab
                  ? 'bg-[#0f172a] text-white'
                  : 'bg-[#f7f8fb] text-[#64748b] hover:bg-[#e2e8f0]'
              }`}
            >
              {t(tab)}
              {/*
                Depth per tab, from the counts endpoint - not just 'Submitted' as before. This is what
                tells the admin the needs_review queue has 47 records even when the list shows 20. The
                'Needs Review' count wears the same violet used everywhere else for that status.
                EVERY ROLE. These were admin-only while GET /ivr/status-counts scoped only
                the 'patient' role and handed a sales_rep GLOBAL numbers. The backend now applies
                the list's own tenant filter to the counts for every role
                (insurance.service.getStatusCounts), so a rep's badges agree with a rep's list.
              */}
              {count > 0 && (
                <span
                  className={`min-w-4 h-4 px-1 text-[12px] font-medium rounded flex items-center justify-center ${
                    tab === 'Needs Review' ? 'bg-[#7e22ce] text-white' : 'bg-[#de524c] text-white'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div {...panelProps}>
        {loading ? (
          <div role="status" aria-live="polite" className="flex items-center justify-center h-40">
            <span className="sr-only">{t('Loading IVR requests...')}</span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
          </div>
        ) : error && ivrData.length === 0 ? null : (
          <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
            <IVRListTable
              records={ivrData}
              admin={admin}
              distributors={distributors}
              t={t}
              locale={locale}
              onView={setSelectedRecord}
              onDelete={setDeleteTarget}
            />
            {/*
            Depth + reachability. Before this, the page discarded the total and showed only page 1, so
            records past the newest 20 could not be browsed at all. "Showing X-Y of TOTAL" makes the depth
            visible; Prev/Next make the whole queue reachable. Controls only render when there is more than
            one page, so a short queue looks exactly as it did.
          */}
            {/* page <= totalPages guards the one-frame transient before the clamp effect fires, so the
              reversed "Showing 41-40 / Page 3 of 2" never paints even for a moment. */}
            {total > 0 && page <= totalPages && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#e2e8f0] bg-[rgba(226,232,240,0.15)]">
                <span className="text-xs font-medium text-[#64748b]">
                  {t('Showing {first}–{last} of {total}', {
                    first: firstRow,
                    last: lastRow,
                    total,
                  })}
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#0f172a] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                    >
                      {t('Previous')}
                    </button>
                    <span className="text-xs font-medium text-[#64748b]">
                      {t('Page {page} of {total}', { page, total: totalPages })}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#0f172a] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                    >
                      {t('Next')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <IVRDetailsModal
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
        onAction={fetchData}
        canAdminister={admin}
      />

      <NewIVRModal open={showNewIVR} onClose={() => setShowNewIVR(false)} onCreated={showCreated} />

      <DeleteRecordModal
        open={!!deleteTarget}
        label={`IVR ${deleteTarget?.requestId || ''}`}
        onCancel={() => setDeleteTarget(null)}
        onDelete={() => ivrService.deleteIVR(deleteTarget._id)}
        onDone={() => {
          setDeleteTarget(null);
          fetchData();
        }}
      />
    </div>
  );
}
